import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { moodColor, moodEmoji } from "@/lib/mood";
import { PageHeader, Spinner } from "@/components/kit";
import { logError } from "@/lib/log";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TZ = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
const clock = (t) => (t.at ? new Date(t.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : t.time);

const DayEmotions = ({ list }) => !list?.length ? null : (
  <div className="flex flex-wrap gap-2 mt-3" data-testid="calendar-day-emotions">
    {list.map((e) => (
      <span key={e.name} className="mm-soft !rounded-full px-3 py-1 text-sm" data-testid={`calendar-day-emotion-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {e.emoji} {e.name} <b className="text-mm-primary">×{e.count}</b>
      </span>
    ))}
  </div>
);

const MonthTop = ({ list }) => (
  <div className="mm-card p-4 mb-4 flex flex-wrap items-center gap-2" data-testid="calendar-month-top">
    <span className="text-sm font-semibold mr-1">This month's top emotions</span>
    {list?.length ? list.map((e, i) => (
      <span key={e.name} className={`!rounded-full px-3 py-1 text-sm ${i === 0 ? "bg-mm-primary text-white" : "mm-soft"}`} data-testid={`calendar-month-top-${i}`}>
        {e.emoji} {e.name} · {e.count}
      </span>
    )) : <span className="text-sm text-mm-muted" data-testid="calendar-month-top-empty">Pick emotions in check-ins or your journal to see patterns here.</span>}
  </div>
);

const DayDetail = ({ date, emotions }) => {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/calendar/day?date=${date}&tz=${TZ}`).then((r) => setD(r.data)).catch(() => setD({ timeline: [] })); }, [date]);
  if (!d) return <Spinner />;
  return (
    <div className="mm-card p-6 mm-fade-up" data-testid="calendar-day-detail">
      <h3 className="font-semibold text-lg">{new Date(date + "T12:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
      {d.summary?.avg_mood != null && <p className="text-sm text-mm-muted mt-1">Avg mood {d.summary.avg_mood} · range {d.summary.min_mood}–{d.summary.max_mood}</p>}
      <DayEmotions list={emotions} />
      <div className="space-y-3 mt-4">
        {d.timeline?.length ? d.timeline.map((t, i) => (
          <div key={`${t.type}-${t.time}-${i}`} className="mm-soft p-4 flex gap-4 items-start">
            <span className="text-2xl">{t.emoji || moodEmoji(t.mood_score || 5)}</span>
            <div className="flex-1">
              <div className="flex justify-between text-sm"><b className="capitalize">{t.type === "micro" ? "Mood snapshot" : "Check-in"}</b><span className="text-mm-muted" data-testid="calendar-entry-time">{clock(t)}</span></div>
              <div className="text-sm">Mood {t.mood_score}/10{t.energy_level ? ` · Energy ${t.energy_level}/10` : ""}</div>
              {(t.journal || t.note) && <p className="text-sm text-mm-muted mt-1">{t.journal || t.note}</p>}
            </div>
          </div>
        )) : <p className="text-mm-muted text-sm">No entries this day.</p>}
      </div>
    </div>
  );
};

export default function CalendarPage() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [emo, setEmo] = useState({ days: {}, month_top: [] });

  useEffect(() => { setData(null); api.get(`/calendar/month?year=${ym.y}&month=${ym.m}&tz=${TZ}`).then((r) => setData(r.data)).catch(() => setData({ days: [] })); }, [ym]);
  useEffect(() => {
    api.get(`/calendar/emotions?year=${ym.y}&month=${ym.m}&tz=${TZ}`).then((r) => setEmo(r.data)).catch((e) => { logError("Emotion calendar load failed:", e); setEmo({ days: {}, month_top: [] }); });
  }, [ym]);

  const shift = (d) => { const dt = new Date(ym.y, ym.m - 1 + d, 1); setYm({ y: dt.getFullYear(), m: dt.getMonth() + 1 }); setSel(null); };
  const offset = new Date(ym.y, ym.m - 1, 1).getDay();
  const label = new Date(ym.y, ym.m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Mood Calendar" subtitle="Your most-picked emotion shows on each day — tap one to see them all" />
      <MonthTop list={emo.month_top} />
      <div className="mm-card p-5 sm:p-6">
        <div className="flex justify-between items-center mb-5">
          <button className="mm-btn-ghost !p-2" onClick={() => shift(-1)} data-testid="calendar-prev-month"><ChevronLeft size={18} /></button>
          <h2 className="font-display text-xl font-bold" data-testid="calendar-month-label">{label}</h2>
          <button className="mm-btn-ghost !p-2" onClick={() => shift(1)} data-testid="calendar-next-month"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2 text-center text-xs text-mm-muted font-semibold mb-2">{WEEKDAYS.map((w) => <div key={w}>{w}</div>)}</div>
        {!data ? <Spinner /> : (
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
            {data.days.map((d) => (
              <button key={d.date} onClick={() => setSel(d.date)} data-testid={`calendar-day-${d.date}`}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-semibold transition-transform hover:scale-105 ${sel === d.date ? "ring-2 ring-offset-2" : ""}`}
                style={{ background: d.avg_mood ? moodColor(Math.round(d.avg_mood)) : "var(--mm-surface-light)", color: d.avg_mood ? "#fff" : "var(--mm-text)", "--tw-ring-color": "var(--mm-primary)" }}>
                {emo.days[d.date]?.[0]
                  ? <span className="text-lg sm:text-2xl leading-none" title={emo.days[d.date][0].name} data-testid={`calendar-day-emoji-${d.date}`}>{emo.days[d.date][0].emoji}</span>
                  : null}
                <span className={emo.days[d.date]?.[0] ? "text-[10px] sm:text-xs leading-tight" : ""}>{d.day}</span>
                {!emo.days[d.date]?.[0] && d.total_entries > 0 && <span className="text-[10px] opacity-90">{d.total_entries}×</span>}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-3 mt-5 text-xs text-mm-muted">
          {[[10, "Excellent"], [7, "Good"], [5, "Okay"], [3, "Low"], [1, "Struggling"]].map(([m, l]) => (
            <span key={l} className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: moodColor(m) }} />{l}</span>
          ))}
        </div>
      </div>
      {sel && <div className="mt-6"><DayDetail date={sel} emotions={emo.days[sel]} /></div>}
    </div>
  );
}
